import { env } from "@/config/env";

export const APP_SHELL_PAGE_KEYS = [
  "command-center",
  "team-operations",
  "listing-intake",
  "livedealz-intake",
  "image-builder",
  "video-slideshow-builder",
  "content-workbench",
  "media-review",
  "final-listing-approval",
  "asset-library-builder",
  "livedealz-review",
  "user-asset-libraries",
  "automation",
  "analytics-audit",
  "roles-permissions",
  "app-shell",
  "suppliers-providers",
  "campaigns",
  "dealz-control",
  "inbox",
  "platform-settings",
] as const;

export type AppShellPageKey = (typeof APP_SHELL_PAGE_KEYS)[number];

export interface Principal {
  id: string;
  workspaceId: string;
  type: "USER" | "SERVICE_ACCOUNT";
  name: string;
  email?: string;
  roles: string[];
  permissions: string[];
  scope: Record<string, unknown>;
  sessionId?: string;
}

export interface LoginResult {
  accessToken: string;
  expiresInSeconds: number;
  user: Principal;
}

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

export interface Paginated<T> {
  items: T[];
  meta: PageMeta;
}

export interface ProblemDetail {
  type: string;
  title: string;
  status: number;
  detail: string;
  errors?: unknown;
  instance?: string;
  requestId?: string;
  timestamp?: string;
}

export class EvzoneApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetail,
  ) {
    super(
      problem.detail || problem.title || `Request failed with HTTP ${status}`,
    );
    this.name = "EvzoneApiError";
  }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

function compactRecord<T extends Record<string, unknown>>(record: T): T {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== undefined),
  ) as T;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value
    .map((item) => asString(item))
    .filter((item): item is string => Boolean(item));
  return items.length ? items : undefined;
}

function normalizeToken(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
}

function normalizeListingPriority(value: unknown): string | undefined {
  const normalized = normalizeToken(String(value ?? ""));
  if (!normalized) return undefined;
  if (
    normalized === "NORMAL" ||
    normalized === "STANDARD" ||
    normalized === "MEDIUM"
  )
    return "MEDIUM";
  if (
    normalized === "CRITICAL" ||
    normalized === "HIGH" ||
    normalized === "LOW"
  )
    return normalized;
  return undefined;
}

function normalizeWorkPriority(value: unknown): string | undefined {
  const normalized = normalizeToken(String(value ?? ""));
  if (!normalized) return undefined;
  if (
    normalized === "NORMAL" ||
    normalized === "MEDIUM" ||
    normalized === "STANDARD"
  )
    return "STANDARD";
  if (
    normalized === "LOW" ||
    normalized === "HIGH" ||
    normalized === "CRITICAL"
  )
    return normalized;
  return undefined;
}

function normalizeRisk(value: unknown): string | undefined {
  const normalized = normalizeToken(String(value ?? ""));
  if (!normalized) return undefined;
  if (normalized === "NORMAL" || normalized === "STANDARD") return "MEDIUM";
  if (["LOW", "MEDIUM", "HIGH", "CRITICAL"].includes(normalized))
    return normalized;
  return undefined;
}

function buildOperationCode(prefix: string, seed: string): string {
  const label = normalizeToken(seed || "TASK").slice(0, 18) || "TASK";
  return `${prefix}-${Date.now().toString(36).toUpperCase()}-${label}`.slice(
    0,
    50,
  );
}

function normalizeSubjectType(value: unknown): string | undefined {
  const normalized = normalizeToken(String(value ?? ""));
  return normalized || undefined;
}

function normalizeSubjectCandidate(body: Record<string, unknown>) {
  const listingId = asString(body.listingId);
  const campaignId = asString(body.campaignId);
  const dealId = asString(body.dealId);
  const supplierId = asString(body.supplierId);
  const subjectId = asString(body.subjectId);
  const subjectType = normalizeSubjectType(body.subjectType);

  if (subjectId) return { subjectId, subjectType };
  if (listingId) return { subjectId: listingId, subjectType: "LISTING" };
  if (campaignId) return { subjectId: campaignId, subjectType: "CAMPAIGN" };
  if (dealId) return { subjectId: dealId, subjectType: "DEAL" };
  if (supplierId)
    return { subjectId: supplierId, subjectType: "SUPPLIER_PROVIDER" };
  return { subjectId: undefined, subjectType };
}

function buildOperationContext(
  body: Record<string, unknown>,
  knownKeys: string[],
): Record<string, unknown> {
  const context = isRecord(body.context) ? { ...body.context } : {};
  const routingContext = isRecord(body.routingContext)
    ? body.routingContext
    : undefined;
  const extras = Object.fromEntries(
    Object.entries(body).filter(([key]) => !knownKeys.includes(key)),
  );

  return compactRecord({
    ...context,
    ...extras,
    ...(routingContext ? { routingContext } : {}),
    listingIds:
      asStringArray(body.listingIds) ?? asStringArray(context.listingIds),
    listingDisplayIds:
      asStringArray(body.listingDisplayIds) ??
      asStringArray(context.listingDisplayIds),
  });
}

function inferWorkType(
  body: Record<string, unknown>,
  subjectType?: string,
): string {
  const explicit = asString(body.workType);
  if (explicit) return normalizeToken(explicit);

  const entity = asString(body.entity);
  if (entity) return normalizeToken(entity);

  if (subjectType) return `${subjectType}_TASK`;
  return "GENERAL_TASK";
}

function inferInboxType(
  body: Record<string, unknown>,
  subjectType?: string,
): string {
  const explicit = asString(body.itemType);
  if (explicit) return normalizeToken(explicit);

  const entity = asString(body.entity);
  if (entity) return `${normalizeToken(entity)}_NOTICE`;

  if (subjectType) return `${subjectType}_NOTICE`;
  return "GENERAL_NOTICE";
}

function normalizeCreateWorkItemPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const knownKeys = [
    "assigneeId",
    "autoRoute",
    "campaignId",
    "code",
    "context",
    "dealId",
    "dueAt",
    "effortUnits",
    "entity",
    "listingDisplayIds",
    "listingId",
    "listingIds",
    "priority",
    "risk",
    "routingContext",
    "subjectId",
    "subjectType",
    "summary",
    "supplierId",
    "teamId",
    "title",
    "workType",
  ];
  const { subjectId: rawSubjectId, subjectType } =
    normalizeSubjectCandidate(body);
  const title = asString(body.title) ?? "Untitled work item";
  const workType = inferWorkType(body, subjectType);
  const context = buildOperationContext(body, knownKeys);

  return compactRecord({
    code: asString(body.code) ?? buildOperationCode("WK", workType),
    workType,
    title,
    summary: asString(body.summary) ?? title,
    priority: normalizeWorkPriority(body.priority),
    assigneeId: isUuid(body.assigneeId) ? body.assigneeId : undefined,
    teamId: isUuid(body.teamId) ? body.teamId : undefined,
    subjectId: isUuid(rawSubjectId) ? rawSubjectId : undefined,
    subjectType,
    dueAt: asString(body.dueAt),
    effortUnits:
      typeof body.effortUnits === "number" ? body.effortUnits : undefined,
    risk: normalizeRisk(body.risk ?? body.priority),
    context,
    autoRoute: typeof body.autoRoute === "boolean" ? body.autoRoute : undefined,
  });
}

function normalizeCreateInboxPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const knownKeys = [
    "assigneeId",
    "campaignId",
    "code",
    "context",
    "dealId",
    "details",
    "dueAt",
    "effortUnits",
    "entity",
    "itemType",
    "linkedWorkItemId",
    "priority",
    "requesterId",
    "risk",
    "source",
    "subjectId",
    "subjectType",
    "summary",
    "supplierId",
    "tags",
    "teamId",
    "title",
  ];
  const { subjectType } = normalizeSubjectCandidate(body);
  const title = asString(body.title) ?? "Inbox item";
  const context = buildOperationContext(body, knownKeys);

  return compactRecord({
    code: asString(body.code) ?? buildOperationCode("INB", title),
    itemType: inferInboxType(body, subjectType),
    title,
    summary: asString(body.summary) ?? title,
    source: asString(body.source) ?? "UI",
    priority: normalizeWorkPriority(body.priority),
    dueAt: asString(body.dueAt),
    assigneeId: isUuid(body.assigneeId) ? body.assigneeId : undefined,
    teamId: isUuid(body.teamId) ? body.teamId : undefined,
    requesterId: isUuid(body.requesterId) ? body.requesterId : undefined,
    risk: normalizeRisk(body.risk ?? body.priority),
    effortUnits:
      typeof body.effortUnits === "number" ? body.effortUnits : undefined,
    tags: asStringArray(body.tags),
    details: asStringArray(body.details),
    context,
    linkedWorkItemId: isUuid(body.linkedWorkItemId)
      ? body.linkedWorkItemId
      : undefined,
  });
}

function normalizeCreateListingPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  return compactRecord({
    code:
      asString(body.code) ??
      buildOperationCode("LST", String(body.title ?? "LISTING")),
    title: asString(body.title),
    supplierProviderId: asString(body.supplierProviderId),
    campaignId: asString(body.campaignId),
    dealId: asString(body.dealId),
    dealType:
      normalizeToken(String(body.dealType ?? "CATALOGUE")) || "CATALOGUE",
    entityType:
      normalizeToken(String(body.entityType ?? "PRODUCT")) || "PRODUCT",
    category: asString(body.category),
    country: asString(body.country),
    sourceLocale: asString(body.sourceLocale),
    targetLocales: asStringArray(body.targetLocales),
    priority: normalizeListingPriority(body.priority),
    dueAt: asString(body.dueAt),
    assigneeId: isUuid(body.assigneeId) ? body.assigneeId : undefined,
    teamId: isUuid(body.teamId) ? body.teamId : undefined,
    libraryOwnerId: isUuid(body.libraryOwnerId)
      ? body.libraryOwnerId
      : undefined,
    libraryPath: asString(body.libraryPath),
    hierarchy: Array.isArray(body.hierarchy) ? body.hierarchy : undefined,
    translationTargets: Array.isArray(body.translationTargets)
      ? body.translationTargets
      : undefined,
    priceDisplay: asString(body.priceDisplay),
    inventoryDisplay: asString(body.inventoryDisplay),
    thumbnailUrl: asString(body.thumbnailUrl),
    posterUrl: asString(body.posterUrl),
    heroUrl: asString(body.heroUrl),
  });
}

function normalizeUpdateListingPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  return compactRecord({
    title: asString(body.title),
    category: asString(body.category),
    country: asString(body.country),
    targetLocales: asStringArray(body.targetLocales),
    priority: normalizeListingPriority(body.priority),
    dueAt: asString(body.dueAt),
    assigneeId:
      body.assigneeId === null
        ? null
        : isUuid(body.assigneeId)
          ? body.assigneeId
          : undefined,
    teamId:
      body.teamId === null
        ? null
        : isUuid(body.teamId)
          ? body.teamId
          : undefined,
    libraryOwnerId:
      body.libraryOwnerId === null
        ? null
        : isUuid(body.libraryOwnerId)
          ? body.libraryOwnerId
          : undefined,
    libraryPath: body.libraryPath === null ? null : asString(body.libraryPath),
    hierarchy: Array.isArray(body.hierarchy) ? body.hierarchy : undefined,
    translationTargets: Array.isArray(body.translationTargets)
      ? body.translationTargets
      : undefined,
    mediaVersion: asString(body.mediaVersion),
    contentVersion: asString(body.contentVersion),
    packageVersion: asString(body.packageVersion),
    slideshowState: asString(body.slideshowState)
      ? normalizeToken(String(body.slideshowState))
      : undefined,
    priceDisplay: asString(body.priceDisplay),
    inventoryDisplay: asString(body.inventoryDisplay),
    thumbnailUrl:
      body.thumbnailUrl === null ? null : asString(body.thumbnailUrl),
    posterUrl: body.posterUrl === null ? null : asString(body.posterUrl),
    heroUrl: body.heroUrl === null ? null : asString(body.heroUrl),
    providerConfig: isRecord(body.providerConfig)
      ? body.providerConfig
      : body.providerConfig === null
        ? null
        : undefined,
    packageManifestRequested:
      typeof body.packageManifestRequested === "boolean"
        ? body.packageManifestRequested
        : undefined,
    translationSync:
      typeof body.translationSync === "boolean"
        ? body.translationSync
        : undefined,
  });
}

function normalizeIntakeDecisionPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const decisionMap: Record<string, string> = {
    ACCEPTED: "ACCEPTED",
    ACCEPT: "ACCEPTED",
    RETURN: "RETURNED",
    RETURNED: "RETURNED",
  };
  const normalized = normalizeToken(String(body.decision ?? ""));
  return {
    decision: decisionMap[normalized] ?? normalized,
    reason: asString(body.reason) ?? "Decision recorded from the intake queue.",
  };
}

function normalizeReleaseDecisionPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const decisionMap: Record<string, string> = {
    APPROVE: "APPROVED",
    APPROVED: "APPROVED",
    STAGE: "STAGED",
    STAGED: "STAGED",
  };
  const normalized = normalizeToken(String(body.decision ?? ""));
  return {
    decision: decisionMap[normalized] ?? normalized,
    reason: asString(body.reason) ?? "Release decision recorded.",
  };
}

function normalizeReviewType(value: unknown): string {
  const normalized = normalizeToken(String(value ?? ""));
  const typeMap: Record<string, string> = {
    MEDIA: "MEDIA",
    CONTENT: "CONTENT",
    LISTING_MEDIA: "LISTING_MEDIA",
    FINAL_LISTING: "FINAL_LISTING",
    LIVEDEALZ: "LIVEDEALZ",
    TRANSLATION: "TRANSLATION",
    RIGHTS: "RIGHTS",
    CAMPAIGN_BRIEF: "CAMPAIGN_BRIEF",
  };
  return typeMap[normalized] ?? "MEDIA";
}

function normalizeReviewPriority(value: unknown): string | undefined {
  const normalized = normalizeToken(String(value ?? ""));
  if (!normalized) return undefined;
  if (normalized === "NORMAL" || normalized === "STANDARD") return "MEDIUM";
  if (["LOW", "MEDIUM", "HIGH", "CRITICAL"].includes(normalized))
    return normalized;
  return undefined;
}

function normalizeReviewSubjectType(value: unknown): string | undefined {
  const normalized = normalizeToken(String(value ?? ""));
  if (!normalized) return undefined;
  if (normalized === "CONTENT") return "CONTENT_ITEM";
  if (normalized === "ASSET") return "ASSET";
  if (normalized === "LISTING") return "LISTING";
  if (normalized === "DEAL") return "DEAL";
  return normalized || undefined;
}

function normalizeReviewSubjectCandidate(body: Record<string, unknown>) {
  const contentId = asString(body.contentId);
  if (contentId) {
    return { subjectId: contentId, subjectType: "CONTENT_ITEM" };
  }

  const assetId = asString(body.assetId);
  if (assetId) {
    return { subjectId: assetId, subjectType: "ASSET" };
  }

  const listingId = asString(body.listingId);
  if (listingId) {
    return { subjectId: listingId, subjectType: "LISTING" };
  }

  const dealId = asString(body.dealId);
  if (dealId) {
    return { subjectId: dealId, subjectType: "DEAL" };
  }

  const subjectId = asString(body.subjectId);
  const subjectType = normalizeReviewSubjectType(body.subjectType);
  return { subjectId, subjectType };
}

function normalizeCreateReviewPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const { subjectId, subjectType } = normalizeReviewSubjectCandidate(body);
  const reviewType = normalizeReviewType(body.reviewType ?? body.type);
  const title = asString(body.title) ?? `${reviewType} review`;
  const snapshotBody = isRecord(body.snapshot) ? body.snapshot : {};
  const snapshot = compactRecord({
    ...snapshotBody,
    projectId: asString(body.projectId),
    note: asString(body.note),
    decision: asString(body.decision),
    status: asString(body.status),
  });

  return compactRecord({
    code: asString(body.code) ?? buildOperationCode("REV", title),
    reviewType,
    subjectType,
    subjectId: isUuid(subjectId) ? subjectId : undefined,
    title,
    priority: normalizeReviewPriority(body.priority),
    dueAt: asString(body.dueAt),
    makerUserId: isUuid(body.makerUserId) ? body.makerUserId : undefined,
    assigneeId: isUuid(body.assigneeId) ? body.assigneeId : undefined,
    teamId: isUuid(body.teamId) ? body.teamId : undefined,
    supplierProviderId: isUuid(body.supplierProviderId)
      ? body.supplierProviderId
      : undefined,
    campaignId: isUuid(body.campaignId) ? body.campaignId : undefined,
    dealId: isUuid(body.dealId) ? body.dealId : undefined,
    assetId: isUuid(body.assetId) ? body.assetId : undefined,
    readinessScore:
      typeof body.readinessScore === "number" ? body.readinessScore : undefined,
    checks: Array.isArray(body.checks) ? body.checks : undefined,
    snapshot: Object.keys(snapshot).length ? snapshot : undefined,
  });
}

function normalizeMediaProjectType(value: unknown): string {
  const normalized = normalizeToken(String(value ?? ""));
  if (
    normalized === "VIDEO" ||
    normalized === "VIDEO_SLIDESHOW" ||
    normalized === "VIDEO_AND_SLIDESHOW"
  ) {
    return "VIDEO_SLIDESHOW";
  }
  return "IMAGE_BUILDER";
}

function buildExternalSourceRefs(
  body: Record<string, unknown>,
  rawSourceIds: string[],
): Array<Record<string, unknown>> {
  const explicitRefs = Array.isArray(body.externalSources)
    ? body.externalSources.filter(isRecord)
    : Array.isArray(body.sourceRefs)
      ? body.sourceRefs.filter(isRecord)
      : [];

  const inferredRefs = rawSourceIds
    .filter((id) => !isUuid(id))
    .map((id) =>
      compactRecord({
        id,
        label: asString(body.sourceLabel) ?? asString(body.name),
        previewUrl: asString(body.previewUrl),
        posterUrl: asString(body.posterUrl),
        source: asString(body.source),
        listingId: asString(body.listingId),
      }),
    )
    .filter((item) => Object.keys(item).length > 0);

  const merged = [...explicitRefs, ...inferredRefs];
  const seen = new Set<string>();

  return merged.filter((item) => {
    const key = String(item.id ?? item.label ?? JSON.stringify(item));
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeCreateMediaProjectPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const rawSourceIds = [
    asString(body.sourceAssetId),
    ...(asStringArray(body.sourceAssetIds) ?? []),
  ].filter((value): value is string => Boolean(value));
  const sourceAssetIds = rawSourceIds.filter((id) => isUuid(id));
  const externalSources = buildExternalSourceRefs(body, rawSourceIds);
  const settings = compactRecord({
    ...(isRecord(body.settings) ? body.settings : {}),
    ...(asString(body.profile) ? { profile: asString(body.profile) } : {}),
    ...(asString(body.background)
      ? { background: asString(body.background) }
      : {}),
    ...(asString(body.cropMode) ? { cropMode: asString(body.cropMode) } : {}),
    ...(typeof body.outputCount === "number"
      ? { outputCount: body.outputCount }
      : {}),
    ...(typeof body.durationSeconds === "number"
      ? { durationSeconds: body.durationSeconds }
      : {}),
    ...(asString(body.aspect) ? { aspect: asString(body.aspect) } : {}),
    ...(externalSources.length ? { externalSources } : {}),
  });

  return compactRecord({
    code:
      asString(body.code) ??
      buildOperationCode(
        "MP",
        String(body.name ?? body.projectType ?? body.type ?? "MEDIA"),
      ),
    name: asString(body.name) ?? "Media project",
    projectType: normalizeMediaProjectType(body.projectType ?? body.type),
    listingId: isUuid(body.listingId) ? body.listingId : undefined,
    campaignId: isUuid(body.campaignId) ? body.campaignId : undefined,
    dealId: isUuid(body.dealId) ? body.dealId : undefined,
    ownerId: isUuid(body.ownerId) ? body.ownerId : undefined,
    sourceAssetIds,
    timeline: Array.isArray(body.timeline) ? body.timeline : undefined,
    outputProfiles: Array.isArray(body.outputProfiles)
      ? body.outputProfiles
      : undefined,
    captionLocales: Array.isArray(body.captionLocales)
      ? body.captionLocales
      : undefined,
    settings: Object.keys(settings).length ? settings : undefined,
  });
}

function normalizeUpdateMediaProjectPayload(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const rawSourceIds = asStringArray(body.sourceAssetIds) ?? [];
  const sourceAssetIds = rawSourceIds.filter((id) => isUuid(id));
  const externalSources = buildExternalSourceRefs(body, rawSourceIds);
  const settings = compactRecord({
    ...(isRecord(body.settings) ? body.settings : {}),
    ...(asString(body.profile) ? { profile: asString(body.profile) } : {}),
    ...(asString(body.background)
      ? { background: asString(body.background) }
      : {}),
    ...(asString(body.cropMode) ? { cropMode: asString(body.cropMode) } : {}),
    ...(typeof body.outputCount === "number"
      ? { outputCount: body.outputCount }
      : {}),
    ...(typeof body.durationSeconds === "number"
      ? { durationSeconds: body.durationSeconds }
      : {}),
    ...(asString(body.aspect) ? { aspect: asString(body.aspect) } : {}),
    ...(typeof body.addedToPackage === "boolean"
      ? { addedToPackage: body.addedToPackage }
      : {}),
    ...(asString(body.restoredFromVersion)
      ? { restoredFromVersion: asString(body.restoredFromVersion) }
      : {}),
    ...(externalSources.length ? { externalSources } : {}),
  });

  return compactRecord({
    name: asString(body.name),
    status: asString(body.status),
    sourceAssetIds: rawSourceIds.length ? sourceAssetIds : undefined,
    timeline: Array.isArray(body.timeline) ? body.timeline : undefined,
    outputProfiles: Array.isArray(body.outputProfiles)
      ? body.outputProfiles
      : undefined,
    captionLocales: Array.isArray(body.captionLocales)
      ? body.captionLocales
      : undefined,
    settings: Object.keys(settings).length ? settings : undefined,
  });
}

export interface RequestOptions extends RequestInit {
  retryOnUnauthorized?: boolean;
  idempotent?: boolean;
  responseType?: "json" | "text" | "blob";
}

export class EvzoneApiClient {
  private accessToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(public readonly baseUrl = env.apiBaseUrl) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  async login(
    email: string,
    password: string,
    rememberMe = true,
    mfaCode?: string,
  ): Promise<LoginResult> {
    const result = await this.request<LoginResult>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, rememberMe, mfaCode }),
      retryOnUnauthorized: false,
    });
    this.accessToken = result.accessToken;
    return result;
  }

  async logout(): Promise<void> {
    try {
      await this.request("/auth/logout", {
        method: "POST",
        retryOnUnauthorized: false,
      });
    } finally {
      this.accessToken = null;
    }
  }

  me(): Promise<Principal> {
    return this.request("/auth/me");
  }

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.request("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  forgotPassword(email: string): Promise<{ message: string }> {
    return this.request("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
      retryOnUnauthorized: false,
    });
  }

  resetPassword(token: string, newPassword: string): Promise<void> {
    return this.request("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
      retryOnUnauthorized: false,
    });
  }

  setupMfa(): Promise<{ secret: string; otpauthUri: string }> {
    return this.request("/auth/mfa/setup", { method: "POST" });
  }

  enableMfa(code: string): Promise<{ enabled: boolean }> {
    return this.request("/auth/mfa/enable", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  disableMfa(password: string, code: string): Promise<{ enabled: boolean }> {
    return this.request("/auth/mfa/disable", {
      method: "POST",
      body: JSON.stringify({ password, code }),
    });
  }

  appShell(): Promise<Record<string, unknown>> {
    return this.request("/dashboard/app-shell");
  }

  commandCenter(): Promise<Record<string, unknown>> {
    return this.request("/dashboard/command-center");
  }

  page<T extends Record<string, unknown> = Record<string, unknown>>(
    key: AppShellPageKey,
    opts?: { page?: number; pageSize?: number },
  ): Promise<T> {
    const params = new URLSearchParams();
    if (opts?.page !== undefined) params.set("page", String(opts.page));
    if (opts?.pageSize !== undefined)
      params.set("pageSize", String(opts.pageSize));
    return this.request(
      `/dashboard/pages/${encodeURIComponent(key)}${this.query(params)}`,
    );
  }

  search(
    query: string,
    limit = 30,
  ): Promise<{ query: string; items: unknown[]; total: number }> {
    return this.request(
      `/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    );
  }

  assets(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/assets${this.query(params)}`);
  }

  assetsAiRefined(): Promise<{ listings: Array<Record<string, unknown>> }> {
    return this.request(`/assets/ai-refined`);
  }

  asset(id: string): Promise<Record<string, unknown>> {
    return this.request(`/assets/${encodeURIComponent(id)}`);
  }

  async uploadAsset(
    file: File,
    fields: Record<string, string | string[] | Record<string, unknown>> = {},
  ): Promise<Record<string, unknown>> {
    const form = new FormData();
    form.append("file", file);
    for (const [key, value] of Object.entries(fields)) {
      form.append(
        key,
        typeof value === "string" ? value : JSON.stringify(value),
      );
    }
    return this.request("/assets/upload", {
      method: "POST",
      body: form,
      idempotent: false,
    });
  }

  signedAssetUrl(
    id: string,
    ttlSeconds = 900,
  ): Promise<{ url: string; expiresInSeconds: number }> {
    return this.request(
      `/assets/${encodeURIComponent(id)}/signed-url?ttlSeconds=${ttlSeconds}`,
      { method: "GET" },
    );
  }

  async uploadAssetVersion(
    id: string,
    file: File,
    fields: Record<string, string | Record<string, unknown>> = {},
  ): Promise<Record<string, unknown>> {
    const form = new FormData();
    form.append("file", file);
    for (const [key, value] of Object.entries(fields)) {
      form.append(
        key,
        typeof value === "string" ? value : JSON.stringify(value),
      );
    }
    return this.request(`/assets/${encodeURIComponent(id)}/versions/upload`, {
      method: "POST",
      body: form,
      idempotent: false,
    });
  }

  async replaceListingSource(
    listingId: string,
    payload: {
      file: File;
      assetId?: string;
      sourceType?: string;
      role?: string;
      changeNote?: string;
    },
  ): Promise<Record<string, unknown>> {
    const form = new FormData();
    form.append("file", payload.file);
    if (payload.assetId) form.append("assetId", payload.assetId);
    if (payload.sourceType) form.append("sourceType", payload.sourceType);
    if (payload.role) form.append("role", payload.role);
    if (payload.changeNote) form.append("changeNote", payload.changeNote);
    return this.request(
      `/listings/${encodeURIComponent(listingId)}/replace-source`,
      {
        method: "POST",
        body: form,
        idempotent: false,
      },
    );
  }

  createImageVariant(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/${encodeURIComponent(id)}/image-variants`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  processAsset(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/${encodeURIComponent(id)}/process`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  assetLibraries(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/assets/libraries${this.query(params)}`);
  }

  assetLibrary(id: string): Promise<Record<string, unknown>> {
    return this.request(`/assets/libraries/${encodeURIComponent(id)}`);
  }

  createLibrary(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/assets/libraries", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateAsset(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  updateAssetRights(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/${encodeURIComponent(id)}/rights`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  archiveAsset(id: string, reason?: string): Promise<Record<string, unknown>> {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    return this.request(`/assets/${encodeURIComponent(id)}${query}`, {
      method: "DELETE",
    });
  }

  bulkLibraryApprove(campaignId: string): Promise<{ approved: number }> {
    return this.request("/assets/bulk/library-approve", {
      method: "POST",
      body: JSON.stringify({ campaignId }),
    });
  }

  refineLibraryAsset(id: string): Promise<Record<string, unknown>> {
    return this.request(`/assets/${encodeURIComponent(id)}/library-refine`, {
      method: "POST",
    });
  }

  submitLibraryAssetForReview(
    id: string,
    body: Record<string, unknown> = {},
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/assets/${encodeURIComponent(id)}/library-submit-review`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  decideLibraryAsset(
    id: string,
    body: { status: "approved" | "rejected"; reason?: string },
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/${encodeURIComponent(id)}/library-decision`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  mediaProjects(): Promise<Record<string, unknown>[]> {
    return this.request("/assets/projects");
  }

  mediaProject(id: string): Promise<Record<string, unknown>> {
    return this.request(`/assets/projects/${encodeURIComponent(id)}`);
  }

  createMediaProject(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/assets/projects", {
      method: "POST",
      body: JSON.stringify(normalizeCreateMediaProjectPayload(body)),
    });
  }

  updateMediaProject(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/projects/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(normalizeUpdateMediaProjectPayload(body)),
    });
  }

  renderMediaProject(
    id: string,
    body: Record<string, unknown> = {},
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/projects/${encodeURIComponent(id)}/render`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  assetPackages(): Promise<Record<string, unknown>[]> {
    return this.request("/assets/packages");
  }

  createAssetPackage(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/assets/packages", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  approveAssetPackage(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/packages/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  addAssetLibraryItem(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/libraries/${encodeURIComponent(id)}/items`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  removeAssetLibraryItem(
    id: string,
    assetId: string,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/assets/libraries/${encodeURIComponent(id)}/items/${encodeURIComponent(assetId)}`,
      { method: "DELETE" },
    );
  }

  content(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/content${this.query(params)}`);
  }

  contentItem(id: string): Promise<Record<string, unknown>> {
    return this.request(`/content/${encodeURIComponent(id)}`);
  }

  createContentVersion(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/content/${encodeURIComponent(id)}/versions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  generateContent(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/content/${encodeURIComponent(id)}/generate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  generateListingContent(
    listingId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(listingId)}/generate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  compareContent(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/content/${encodeURIComponent(id)}/compare`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  approveContentVersion(
    id: string,
    versionId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/content/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/approve`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  approveOriginalContent(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/content/${encodeURIComponent(id)}/approve-original`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  approveListingOriginalContent(
    listingId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/content/listing/${encodeURIComponent(listingId)}/approve-original`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  updateContentVersion(
    id: string,
    versionId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/content/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  }

  createTranslation(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/content/${encodeURIComponent(id)}/translations`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateTranslation(
    id: string,
    locale: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/content/${encodeURIComponent(id)}/translations/${encodeURIComponent(locale)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  }

  updateTranslationFields(
    id: string,
    locale: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/content/${encodeURIComponent(id)}/translations/${encodeURIComponent(locale)}/versions`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  approveTranslation(
    id: string,
    locale: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/content/${encodeURIComponent(id)}/translations/${encodeURIComponent(locale)}/approve`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  reviews(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/reviews${this.query(params)}`);
  }

  review(id: string): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}`);
  }

  reviewPackage(id: string): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/package`);
  }

  completeReview(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/complete`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  bulkCompleteReviews(body: {
    ids: string[];
    decision: string;
    reason: string;
  }): Promise<Record<string, unknown>> {
    return this.request("/reviews/bulk/complete", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  createReview(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/reviews", {
      method: "POST",
      body: JSON.stringify(normalizeCreateReviewPayload(body)),
    });
  }

  assignReview(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/assignment`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  addReviewComment(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/comments`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  claimReview(id: string): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/claim`, {
      method: "POST",
    });
  }

  updateReviewChecks(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/checks`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  addReviewDecision(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/decisions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  reopenReview(id: string, reason: string): Promise<Record<string, unknown>> {
    return this.request(`/reviews/${encodeURIComponent(id)}/reopen`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }

  teams(): Promise<Record<string, unknown>[]> {
    return this.request("/operations/teams");
  }

  users(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/users${this.query(params)}`);
  }

  createTeam(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/operations/teams", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  addTeamMember(
    teamId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/operations/teams/${encodeURIComponent(teamId)}/members`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  updateTeamMember(
    teamId: string,
    memberId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/operations/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(memberId)}`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    );
  }

  removeTeamMember(
    teamId: string,
    memberId: string,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/operations/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(memberId)}`,
      { method: "DELETE" },
    );
  }

  updateTeam(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/operations/teams/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  routingPolicies(): Promise<Record<string, unknown>[]> {
    return this.request("/operations/routing-policies");
  }

  createRoutingPolicy(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/operations/routing-policies", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateRoutingPolicy(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/operations/routing-policies/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    );
  }

  testRoutingPolicy(
    context: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/operations/routing-policies/test", {
      method: "POST",
      body: JSON.stringify(context),
    });
  }

  workItems(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/operations/work-items${this.query(params)}`);
  }

  createWorkItem(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/operations/work-items", {
      method: "POST",
      body: JSON.stringify(normalizeCreateWorkItemPayload(body)),
    });
  }

  assignWorkItem(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/operations/work-items/${encodeURIComponent(id)}/assign`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  updateWorkItem(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/operations/work-items/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  autoRouteWorkItem(id: string): Promise<Record<string, unknown>> {
    return this.request(
      `/operations/work-items/${encodeURIComponent(id)}/auto-route`,
      {
        method: "POST",
      },
    );
  }

  completeWorkItem(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/operations/work-items/${encodeURIComponent(id)}/complete`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  workItem(id: string): Promise<Record<string, unknown>> {
    return this.request(`/operations/work-items/${encodeURIComponent(id)}`);
  }

  myWorkSummary(): Promise<Record<string, unknown>> {
    return this.request("/operations/my-work");
  }

  createInboxItem(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/operations/inbox", {
      method: "POST",
      body: JSON.stringify(normalizeCreateInboxPayload(body)),
    });
  }

  inbox(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/operations/inbox${this.query(params)}`);
  }

  markInboxRead(id: string, unread = false): Promise<Record<string, unknown>> {
    return this.request(`/operations/inbox/${encodeURIComponent(id)}/read`, {
      method: "PATCH",
      body: JSON.stringify({ unread }),
    });
  }

  resolveInbox(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/operations/inbox/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  suppliers(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/supplier-providers${this.query(params)}`);
  }

  createSupplier(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/supplier-providers", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateSupplier(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/supplier-providers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  supplierStatus(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/supplier-providers/${encodeURIComponent(id)}/status`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  supplier(id: string): Promise<Record<string, unknown>> {
    return this.request(`/supplier-providers/${encodeURIComponent(id)}`);
  }

  addSupplierDocument(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/supplier-providers/${encodeURIComponent(id)}/documents`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  verifySupplierDocument(
    id: string,
    documentId: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/supplier-providers/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/verify`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  updateSupplierQuality(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/supplier-providers/${encodeURIComponent(id)}/quality`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  campaigns(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/campaigns${this.query(params)}`);
  }

  createCampaign(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/campaigns", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateCampaign(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/campaigns/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  campaignStatus(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/campaigns/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  campaign(id: string): Promise<Record<string, unknown>> {
    return this.request(`/campaigns/${encodeURIComponent(id)}`);
  }

  approveCampaignBrief(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/campaigns/${encodeURIComponent(id)}/brief/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  deals(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/deals${this.query(params)}`);
  }

  createDeal(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/deals", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateDeal(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/deals/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  dealStatus(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/deals/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  deal(id: string): Promise<Record<string, unknown>> {
    return this.request(`/deals/${encodeURIComponent(id)}`);
  }

  updateDealTelemetry(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/deals/${encodeURIComponent(id)}/telemetry`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  listings(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/listings${this.query(params)}`);
  }

  listingCounts(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Record<string, number>> {
    return this.request(`/listings/counts${this.query(params)}`);
  }

  listing(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}`);
  }

  importCreatorListings(
    source: string,
    query: { page?: number; pageSize?: number } = {},
  ): Promise<Record<string, unknown>> {
    const params = new URLSearchParams();
    if (query.page !== undefined) params.set("page", String(query.page));
    if (query.pageSize !== undefined)
      params.set("pageSize", String(query.pageSize));
    return this.request(
      `/external/import/${encodeURIComponent(source)}${this.query(params)}`,
      {
        method: "POST",
      },
    );
  }

  creatorListingSnapshot(id: string): Promise<Record<string, unknown>> {
    return this.request(`/external/listings/${encodeURIComponent(id)}/creator`);
  }

  generateListingFields(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/generate`, {
      method: "POST",
    });
  }

  aiRefineListing(
    id: string,
    options?: { redo?: boolean; assetId?: string; assetIds?: string[] },
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/ai-refine`, {
      method: "POST",
      body: JSON.stringify({
        redo: options?.redo === true,
        assetId: options?.assetId,
        assetIds: options?.assetIds,
      }),
    });
  }

  testBackgroundRemovalPrompt(
    id: string,
    prompt: string,
    assetId?: string,
  ): Promise<{ previewUrl: string; filename: string; mimeType: string }> {
    return this.request(
      `/listings/${encodeURIComponent(id)}/test-background-prompt`,
      {
        method: "POST",
        body: JSON.stringify({ prompt, assetId }),
      },
    );
  }

  getListingRefinedImages(id: string): Promise<Record<string, unknown>[]> {
    return this.request(`/listings/${encodeURIComponent(id)}/refined-images`, {
      method: "GET",
    });
  }

  generateListingSlideshow(
    id: string,
    body: { imageIds?: string[]; force?: boolean } = {},
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/slideshow`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  generateListingSlideshowV2(
    id: string,
    body: { imageIds?: string[]; force?: boolean } = {},
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/slideshows/generate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  runListingMediaQualityCheck(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/media/quality-check`, {
      method: "POST",
    });
  }

  processListingMedia(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/media/process`, {
      method: "POST",
    });
  }

  reprocessListingMedia(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/media/reprocess`, {
      method: "POST",
    });
  }

  getListingMediaAssets(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/media/assets`, {
      method: "GET",
    });
  }

  approveListingProcessedMedia(
    id: string,
    body: { assetIds?: string[] } = {},
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/media/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  slideshowTemplates(): Promise<Record<string, unknown>[]> {
    return this.request("/slideshows/templates");
  }

  slideshowCapabilities(): Promise<Record<string, unknown>> {
    return this.request("/slideshows/capabilities");
  }

  previewSlideshowStoryboard(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/slideshows/storyboard/preview", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  listSlideshowRenders(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/slideshows/renders${this.query(params)}`);
  }

  slideshowSources(listingId: string): Promise<{ listingId: string; maxImages: number; groups: Record<string, Array<{ id: string; name: string; width: number; height: number; previewUrl: string }>> }> {
    return this.request(`/slideshows/listings/${encodeURIComponent(listingId)}/sources`);
  }

  createSlideshowRender(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/slideshows/renders", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  getSlideshowRender(id: string): Promise<Record<string, unknown>> {
    return this.request(`/slideshows/renders/${encodeURIComponent(id)}`);
  }

  retrySlideshowRender(id: string): Promise<Record<string, unknown>> {
    return this.request(`/slideshows/renders/${encodeURIComponent(id)}/retry`, {
      method: "POST",
      body: JSON.stringify({ reason: "Operator retry" }),
    });
  }

  cancelSlideshowRender(id: string): Promise<Record<string, unknown>> {
    return this.request(`/slideshows/renders/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason: "Operator cancellation" }),
    });
  }

  submitListingMediaReview(id: string): Promise<Record<string, unknown>> {
    return this.request(
      `/listings/${encodeURIComponent(id)}/submit-media-review`,
      {
        method: "POST",
      },
    );
  }

  createListing(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/listings", {
      method: "POST",
      body: JSON.stringify(normalizeCreateListingPayload(body)),
    });
  }

  updateListing(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(normalizeUpdateListingPayload(body)),
    });
  }

  deleteListing(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  intakeDecision(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/intake-decision`, {
      method: "POST",
      body: JSON.stringify(normalizeIntakeDecisionPayload(body)),
    });
  }

  runPreflight(id: string): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/preflight`, {
      method: "POST",
    });
  }

  evaluateFinalGates(id: string): Promise<Record<string, unknown>> {
    return this.request(
      `/listings/${encodeURIComponent(id)}/final-gates/evaluate`,
      { method: "POST" },
    );
  }

  releaseDecision(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/listings/${encodeURIComponent(id)}/release-decision`,
      {
        method: "POST",
        body: JSON.stringify(normalizeReleaseDecisionPayload(body)),
      },
    );
  }

  bulkReleaseDecision(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/bulk/release-decision`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  removeFromFinalApproval(id: string): Promise<Record<string, unknown>> {
    return this.request(
      `/listings/${encodeURIComponent(id)}/remove-from-final-approval`,
      { method: "POST" },
    );
  }

  publishListing(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/listings/${encodeURIComponent(id)}/publish`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  listingIntakeQueue(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/listings/queues/intake${this.query(params)}`);
  }

  finalApprovalQueue(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/listings/queues/final-approval${this.query(params)}`);
  }

  automationProviders(): Promise<Record<string, unknown>[]> {
    return this.request("/automation/providers");
  }

  createAutomationProvider(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/automation/providers", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateAutomationProvider(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/automation/providers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  testAutomationProvider(id: string): Promise<Record<string, unknown>> {
    return this.request(
      `/automation/providers/${encodeURIComponent(id)}/test`,
      { method: "POST" },
    );
  }

  automationPrompts(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/automation/prompts${this.query(params)}`);
  }

  createAutomationPrompt(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/automation/prompts", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateAutomationPrompt(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/automation/prompts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  cloneAutomationPrompt(
    id: string,
    versionName: string,
  ): Promise<Record<string, unknown>> {
    return this.request(`/automation/prompts/${encodeURIComponent(id)}/clone`, {
      method: "POST",
      body: JSON.stringify({ versionName }),
    });
  }

  publishAutomationPrompt(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/automation/prompts/${encodeURIComponent(id)}/publish`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  automationRecipes(): Promise<Record<string, unknown>[]> {
    return this.request("/automation/recipes");
  }

  createAutomationRecipe(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/automation/recipes", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateAutomationRecipe(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/automation/recipes/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  automationJobs(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/automation/jobs${this.query(params)}`);
  }

  enqueueAutomationJob(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/automation/jobs", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  retryAutomationJob(id: string): Promise<Record<string, unknown>> {
    return this.request(`/automation/jobs/${encodeURIComponent(id)}/retry`, {
      method: "POST",
    });
  }

  cancelAutomationJob(id: string): Promise<Record<string, unknown>> {
    return this.request(`/automation/jobs/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
    });
  }

  automationJob(id: string): Promise<Record<string, unknown>> {
    return this.request(`/automation/jobs/${encodeURIComponent(id)}`);
  }

  automationWebhooks(): Promise<Record<string, unknown>[]> {
    return this.request("/automation/webhooks");
  }

  createAutomationWebhook(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/automation/webhooks", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateAutomationWebhook(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/automation/webhooks/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  testAutomationWebhook(id: string): Promise<Record<string, unknown>> {
    return this.request(`/automation/webhooks/${encodeURIComponent(id)}/test`, {
      method: "POST",
    });
  }

  webhookDeliveries(endpointId?: string): Promise<Record<string, unknown>[]> {
    const params = endpointId
      ? new URLSearchParams({ endpointId })
      : new URLSearchParams();
    return this.request(`/automation/webhook-deliveries${this.query(params)}`);
  }

  creatorWebhookDeliveries(
    status?: string,
  ): Promise<Record<string, unknown>[]> {
    const params = status
      ? new URLSearchParams({ status })
      : new URLSearchParams();
    return this.request(
      `/external/creator-webhook-deliveries${this.query(params)}`,
    );
  }

  retryCreatorWebhookDelivery(id: string): Promise<Record<string, unknown>> {
    return this.request(
      `/external/creator-webhook-deliveries/${encodeURIComponent(id)}/retry`,
      {
        method: "POST",
      },
    );
  }

  audit(params: URLSearchParams = new URLSearchParams()): Promise<unknown> {
    return this.request(`/governance/audit${this.query(params)}`);
  }

  verifyAuditChain(): Promise<{
    valid: boolean;
    checked: number;
    brokenAt?: number;
  }> {
    return this.request("/governance/audit/verify-chain");
  }

  createDirectSetting(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/governance/settings/direct", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  createChangeSet(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/governance/change-sets", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  decideChangeSet(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/governance/change-sets/${encodeURIComponent(id)}/decision`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  publishChangeSet(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/governance/change-sets/${encodeURIComponent(id)}/publish`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  settings(
    params: URLSearchParams = new URLSearchParams(),
  ): Promise<Paginated<Record<string, unknown>>> {
    return this.request(`/governance/settings${this.query(params)}`);
  }

  setting(kind: string, key: string): Promise<Record<string, unknown>> {
    return this.request(
      `/governance/settings/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`,
    );
  }

  effectiveIntakePrompts(): Promise<{
    textPrompt: string;
    photoPrompt: string;
    isDefault: boolean;
  }> {
    return this.request(
      "/governance/settings/intake-automation/prompts/effective",
    );
  }

  changeSets(): Promise<Record<string, unknown>[]> {
    return this.request("/governance/change-sets");
  }

  analyticsOverview(): Promise<Record<string, unknown>> {
    return this.request("/governance/analytics/overview");
  }

  analyticsThroughput(days?: number): Promise<Record<string, unknown>> {
    const query = days != null ? `?days=${days}` : "";
    return this.request(`/governance/analytics/throughput${query}`);
  }

  analyticsSLA(): Promise<Record<string, unknown>> {
    return this.request("/governance/analytics/sla");
  }

  analyticsAuditActivity(days?: number): Promise<Record<string, unknown>> {
    const query = days != null ? `?days=${days}` : "";
    return this.request(`/governance/analytics/audit-activity${query}`);
  }

  exports(): Promise<Record<string, unknown>[]> {
    return this.request("/governance/exports");
  }

  createExport(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/governance/exports", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  downloadExport(id: string): Promise<Blob> {
    return this.request<Blob>(
      `/governance/exports/${encodeURIComponent(id)}/download`,
      { responseType: "blob" },
    );
  }

  createUser(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/users", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateUser(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  setUserRoles(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/users/${encodeURIComponent(id)}/roles`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  user(id: string): Promise<Record<string, unknown>> {
    return this.request(`/users/${encodeURIComponent(id)}`);
  }

  roles(): Promise<Record<string, unknown>[]> {
    return this.request("/roles");
  }

  permissions(): Promise<Record<string, unknown>[]> {
    return this.request("/permissions");
  }

  accessRequests(status?: string): Promise<Record<string, unknown>[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    return this.request(`/access-requests${query}`);
  }

  requestAccess(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/access-requests", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  serviceAccounts(): Promise<Record<string, unknown>[]> {
    return this.request("/service-accounts");
  }

  createServiceAccount(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/service-accounts", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  rotateServiceAccount(id: string): Promise<Record<string, unknown>> {
    return this.request(`/service-accounts/${encodeURIComponent(id)}/rotate`, {
      method: "POST",
    });
  }

  toggleServiceAccount(
    id: string,
    enabled: boolean,
  ): Promise<Record<string, unknown>> {
    return this.request(`/service-accounts/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
  }

  createRole(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/roles", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateRole(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/roles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  setRolePermissions(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/roles/${encodeURIComponent(id)}/permissions`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  decideAccessRequest(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/access-requests/${encodeURIComponent(id)}/decision`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  createContent(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/content", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateContent(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/content/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  createExternalListing(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/external/listings", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  createExternalAsset(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/external/assets", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  notifyCreatorAsset(
    id: string,
    body: {
      status: "approved" | "rejected" | "changes_requested";
      reason?: string;
    },
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/external/creator/assets/${encodeURIComponent(id)}/notify`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  }

  bulkImportCreatorListings(
    items: Record<string, unknown>[],
  ): Promise<Record<string, unknown>> {
    return this.request("/external/listings/bulk-import", {
      method: "POST",
      body: JSON.stringify({ items }),
    });
  }

  importFromCreator(
    source: string,
    query: { page?: number; pageSize?: number } = {},
  ): Promise<Record<string, unknown>> {
    const params = new URLSearchParams();
    if (query.page != null) params.set("page", String(query.page));
    if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
    return this.request(
      `/external/import/${encodeURIComponent(source)}?${params.toString()}`,
    );
  }

  proxyToCreator(
    path: string,
    options: { method?: string; body?: unknown } = {},
  ): Promise<unknown> {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    return this.request(`/external/proxy${normalized}`, {
      method: options.method ?? "GET",
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  }

  createAssetLibrary(
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/assets/libraries", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  updateAssetLibrary(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/assets/libraries/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  async request<T = unknown>(
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const {
      retryOnUnauthorized = true,
      idempotent = true,
      responseType = "json",
      ...init
    } = options;
    const headers = new Headers(init.headers);
    const isFormData =
      typeof FormData !== "undefined" && init.body instanceof FormData;

    if (
      init.body !== undefined &&
      !isFormData &&
      !headers.has("Content-Type")
    ) {
      headers.set("Content-Type", "application/json");
    }
    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }
    if (this.accessToken)
      headers.set("Authorization", `Bearer ${this.accessToken}`);

    const method = (init.method ?? "GET").toUpperCase();
    if (
      idempotent &&
      ["POST", "PUT", "PATCH"].includes(method) &&
      !isFormData &&
      !headers.has("Idempotency-Key")
    ) {
      headers.set("Idempotency-Key", this.randomId());
    }

    const response = await fetch(
      `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`,
      {
        ...init,
        headers,
        credentials: "include",
      },
    );

    if (
      response.status === 401 &&
      retryOnUnauthorized &&
      !path.startsWith("/auth/")
    ) {
      const refreshed = await this.refresh();
      if (refreshed)
        return this.request<T>(path, {
          ...options,
          retryOnUnauthorized: false,
        });
    }

    if (!response.ok) throw await this.toError(response);
    if (response.status === 204) return undefined as T;

    if (responseType === "blob") return (await response.blob()) as unknown as T;
    if (responseType === "text") return (await response.text()) as T;

    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json"))
      return (await response.json()) as T;
    return (await response.text()) as T;
  }

  async refresh(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: "{}",
        });
        if (!response.ok) {
          this.accessToken = null;
          return false;
        }
        const result = (await response.json()) as LoginResult;
        if (!result.accessToken) {
          this.accessToken = null;
          return false;
        }
        this.accessToken = result.accessToken;
        return true;
      } catch {
        this.accessToken = null;
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  private query(params: URLSearchParams): string {
    const value = params.toString();
    return value ? `?${value}` : "";
  }

  private randomId(): string {
    return (
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`
    );
  }

  private async toError(response: Response): Promise<EvzoneApiError> {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = {
        type: "about:blank",
        title: response.statusText || "Request failed",
        status: response.status,
        detail:
          response.statusText || `Request failed with HTTP ${response.status}`,
      };
    }
    return new EvzoneApiError(response.status, problem);
  }
}

export const api = new EvzoneApiClient();
